/**
 * SLA resolution date filter presets for tasks-datatable.
 *
 * Implements DatePresetConfig interface for SLA resolution date filtering.
 * Provides preset IDs, detection logic, and date value application.
 *
 * Standardized preset IDs: before_today, today, none
 */

import { getToday, getYesterday } from '@/utils/dateUtils';

import type {
  DateFilterState,
  DatePresetConfig,
  PresetDateValues,
} from '../../common/DataTable/filterPresets/types';

// ============================================================================
// PRESET IDS - Single source of truth for SLA resolution preset values
// ============================================================================

export const SLA_RESOLUTION_PRESET_IDS = {
  BEFORE_TODAY: 'before_today',
  TODAY: 'today',
  NONE: 'none',
} as const;

export type SlaResolutionPresetId =
  (typeof SLA_RESOLUTION_PRESET_IDS)[keyof typeof SLA_RESOLUTION_PRESET_IDS];

// ============================================================================
// PRESET DETECTION - Determine which preset matches current filter state
// ============================================================================

/**
 * Determine which preset (if any) is currently active based on filter state.
 * Returns array for compatibility with chip selection APIs.
 */
export function getActiveSlaResolutionPreset(
  state: DateFilterState,
): SlaResolutionPresetId[] {
  const { startDate, endDate, noDueDate } = state;

  if (noDueDate) {
    return [SLA_RESOLUTION_PRESET_IDS.NONE];
  }

  const today = getToday();
  const yesterday = getYesterday();

  // "Today" = both start and end are today
  if (startDate === today && endDate === today) {
    return [SLA_RESOLUTION_PRESET_IDS.TODAY];
  }

  // "Before Today" = SLA resolution target before today (end is yesterday, inclusive)
  if (!startDate && endDate === yesterday) {
    return [SLA_RESOLUTION_PRESET_IDS.BEFORE_TODAY];
  }

  return [];
}

// ============================================================================
// PRESET APPLICATION - Compute date values for a given preset
// ============================================================================

/**
 * Get the date values that should be applied for a given preset.
 * Returns undefined values to indicate "should be cleared".
 */
export function getPresetSlaResolutionValues(
  presetId: string,
): PresetDateValues {
  const today = getToday();
  const yesterday = getYesterday();

  switch (presetId) {
    case SLA_RESOLUTION_PRESET_IDS.BEFORE_TODAY:
      return { startDate: undefined, endDate: yesterday, noDueDate: false };

    case SLA_RESOLUTION_PRESET_IDS.TODAY:
      return { startDate: today, endDate: today, noDueDate: false };

    case SLA_RESOLUTION_PRESET_IDS.NONE:
      return { startDate: undefined, endDate: undefined, noDueDate: true };

    default:
      // Unknown preset - clear everything
      return { startDate: undefined, endDate: undefined, noDueDate: false };
  }
}

// ============================================================================
// EXPORT CONFIG OBJECT (implements DatePresetConfig interface)
// ============================================================================

export const slaResolutionPresetConfig: DatePresetConfig<SlaResolutionPresetId> =
  {
    IDS: SLA_RESOLUTION_PRESET_IDS,
    getActivePreset: getActiveSlaResolutionPreset,
    getPresetValues: getPresetSlaResolutionValues,
  };
